import { useState, useEffect } from "react";
import { useAuth, API } from "@/App";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Checkbox } from "@/components/ui/checkbox";
import { Users, Ticket, Activity, LogOut, Plus, Trash2, RotateCcw, Eye, FileDown, Archive, ArchiveRestore, AlertTriangle, ChevronDown } from "lucide-react";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import ThemeToggle from "@/components/ThemeToggle";

export default function SuperadminDashboard() {
  const { logout, user } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [codes, setCodes] = useState([]);
  const [simulations, setSimulations] = useState([]);
  const [instructors, setInstructors] = useState([]);
  const [showCodeDialog, setShowCodeDialog] = useState(false);
  const [showSimDialog, setShowSimDialog] = useState(false);
  const [docsOpen, setDocsOpen] = useState(false);
  const [codeForm, setCodeForm] = useState({ code_type: "registration", role: "student", max_uses: 1, note: "" });
  const [simForm, setSimForm] = useState({ name: "", instructor_id: "", rounds_count: 8, max_teams: 8 });
  const [selectedUsers, setSelectedUsers] = useState(new Set());
  const [selectedCodes, setSelectedCodes] = useState(new Set());
  const [selectedSims, setSelectedSims] = useState(new Set());
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [archivedSims, setArchivedSims] = useState([]);
  const [archivedUsers, setArchivedUsers] = useState([]);
  const [archivedCodes, setArchivedCodes] = useState([]);

  useEffect(() => { loadAll(); }, []);

  const loadAll = async () => {
    try {
      const [u, c, s, a, au, ac] = await Promise.all([
        axios.get(`${API}/admin/users`),
        axios.get(`${API}/admin/codes`),
        axios.get(`${API}/admin/simulations`),
        axios.get(`${API}/admin/archived-simulations`),
        axios.get(`${API}/admin/archived-users`),
        axios.get(`${API}/admin/archived-codes`),
      ]);
      setUsers(u.data); setCodes(c.data); setSimulations(s.data); setArchivedSims(a.data);
      setArchivedUsers(au.data); setArchivedCodes(ac.data);
      setInstructors(u.data.filter(x => x.role === "instructor"));
      setSelectedUsers(new Set()); setSelectedCodes(new Set()); setSelectedSims(new Set());
    } catch (e) { toast.error("Error al cargar datos"); }
  };

  const extractError = (e) => {
    const detail = e.response?.data?.detail;
    if (typeof detail === "string") return detail;
    if (Array.isArray(detail)) return detail.map(d => d.msg || JSON.stringify(d)).join(", ");
    return "Error";
  };

  const createCode = async () => {
    try { await axios.post(`${API}/admin/codes`, codeForm); toast.success(t("admin.codeCreated") || "Código creado"); setShowCodeDialog(false); loadAll(); }
    catch (e) { toast.error(extractError(e)); }
  };
  const createSimulation = async () => {
    try { await axios.post(`${API}/simulations`, { ...simForm, scenario: "default" }); toast.success(t("admin.simCreated") || "Simulación creada"); setShowSimDialog(false); loadAll(); }
    catch (e) { toast.error(extractError(e)); }
  };
  const downloadDoc = async (endpoint, filename) => {
    try {
      const res = await axios.get(`${API}/${endpoint}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a"); a.href = url; a.download = filename;
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch { toast.error("Error downloading"); }
  };

  const DOCS = [
    { label: "Student Guide (PDF)", endpoint: "student-guide-pdf", file: "MedBoard_Student_Guide_ENGESP.pdf" },
    { label: "Instructor Guide (PDF)", endpoint: "instructor-guide-pdf", file: "MedBoard_Instructor_Guide_ENGESP.pdf" },
    { label: "CEO Strategy", endpoint: "docs/ceo-strategy", file: "MedBoard_CEO_Strategy.pdf" },
    { label: "Instructor Playbook", endpoint: "docs/instructor-playbook", file: "MedBoard_Instructor_Playbook.pdf" },
    { label: "Masterclass ESP", endpoint: "docs/instructor-masterclass", file: "MedBoard_Instructor_MasterclassESP.pdf" },
    { label: "Strategic Playbook", endpoint: "docs/strategic-playbook", file: "MedBoard_Strategic_Playbook.pdf" },
  ];

  const deleteUser = async (id) => {
    try {
      const res = await axios.get(`${API}/admin/users/${id}/delete-info`);
      setConfirmDelete({ type: "user", id, ...res.data });
    } catch { toast.error("Error"); }
  };
  const resetPassword = async (id) => { try { const res = await axios.put(`${API}/admin/users/${id}/reset-password`); setUsers(prev => prev.map(u => u.id === id ? { ...u, plain_password: res.data.new_password } : u)); toast.success(`Nueva contraseña: ${res.data.new_password}`); } catch { toast.error("Error"); } };
  const deleteCode = async (id) => {
    setConfirmDelete({ type: "code", id });
  };
  const requestDeleteSim = async (id) => {
    try {
      const res = await axios.get(`${API}/admin/simulations/${id}/delete-info`);
      setConfirmDelete({ type: "simulation", id, ...res.data });
    } catch { toast.error("Error"); }
  };
  const confirmDeleteAction = async () => {
    if (!confirmDelete) return;
    const { type, id } = confirmDelete;
    try {
      if (type === "simulation") await axios.delete(`${API}/admin/simulations/${id}`);
      else if (type === "user") await axios.delete(`${API}/admin/users/${id}`);
      else if (type === "code") await axios.delete(`${API}/admin/codes/${id}`);
      toast.success("Moved to trash / Movido a papelera");
      setConfirmDelete(null);
      loadAll();
    } catch { toast.error("Error"); }
  };
  const restoreItem = async (type, id) => {
    try {
      await axios.post(`${API}/admin/${type}/${id}/restore`);
      toast.success("Restored / Restaurado");
      loadAll();
    } catch { toast.error("Error"); }
  };
  const permanentDelete = async (type, id) => {
    try {
      await axios.delete(`${API}/admin/${type}/${id}/permanent`);
      toast.success("Permanently deleted / Eliminado permanentemente");
      loadAll();
    } catch { toast.error("Error"); }
  };

  const bulkDeleteUsers = async () => { if (!selectedUsers.size) return; try { const r = await axios.post(`${API}/admin/users/bulk-delete`, { ids: [...selectedUsers] }); toast.success(`${r.data.deleted} eliminados`); loadAll(); } catch (e) { toast.error(e.response?.data?.detail || "Error"); } };
  const bulkDeleteCodes = async () => { if (!selectedCodes.size) return; try { const r = await axios.post(`${API}/admin/codes/bulk-delete`, { ids: [...selectedCodes] }); toast.success(`${r.data.deleted} eliminados`); loadAll(); } catch (e) { toast.error(e.response?.data?.detail || "Error"); } };
  const bulkDeleteSims = async () => { if (!selectedSims.size) return; try { const r = await axios.post(`${API}/admin/simulations/bulk-delete`, { ids: [...selectedSims] }); toast.success(`${r.data.deleted} eliminados`); loadAll(); } catch (e) { toast.error(e.response?.data?.detail || "Error"); } };

  const toggle = (set, setter, id) => { const n = new Set(set); if (n.has(id)) n.delete(id); else n.add(id); setter(n); };
  const toggleAll = (items, set, setter) => { setter(set.size === items.length ? new Set() : new Set(items.map(i => i.id))); };

  const stats = { users: users.length, sims: simulations.length, codes: codes.length, active: simulations.filter(s => s.status === "active").length };

  return (
    <div className="min-h-screen bg-[#F8FAFC]" data-testid="superadmin-dashboard">
      <header className="bg-white/90 backdrop-blur-xl border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/logo-medsim.png" alt="MedSim" className="h-8 rounded-md" />
            <span className="text-lg font-heading font-semibold text-slate-900 tracking-tight">MedSim</span>
            <span className="text-xs text-slate-400 font-medium">/</span>
            <span className="text-sm text-slate-500">{t("admin.title")}</span>
          </div>
          <div className="flex items-center gap-3">
            <div className="relative">
              <Button variant="outline" size="sm" onClick={() => setDocsOpen(!docsOpen)} data-testid="docs-dropdown-btn">
                <FileDown className="w-4 h-4 mr-1.5" />Documents<ChevronDown className="w-3.5 h-3.5 ml-1.5" />
              </Button>
              {docsOpen && (
                <div className="absolute right-0 top-full mt-1 w-64 bg-white dark:bg-gray-800 border border-slate-200 dark:border-slate-700 rounded-lg shadow-lg z-50 py-1" data-testid="docs-menu">
                  {DOCS.map((doc) => (
                    <button key={doc.endpoint} className="w-full text-left px-4 py-2 text-sm text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700 flex items-center gap-2 transition-colors" onClick={() => { downloadDoc(doc.endpoint, doc.file); setDocsOpen(false); }} data-testid={`doc-${doc.endpoint}`}>
                      <FileDown className="w-3.5 h-3.5 text-slate-400 shrink-0" />{doc.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
            <ThemeToggle />
            <LanguageSwitcher />
            <span className="text-sm text-slate-500">{user?.email}</span>
            <Button variant="ghost" size="sm" onClick={logout} data-testid="logout-btn"><LogOut className="w-4 h-4" /></Button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 py-8 space-y-6">
        {/* KPI STRIP */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[
            { label: t("admin.totalUsers"), value: stats.users, icon: Users, color: "text-sky-600" },
            { label: t("admin.totalSimulations"), value: stats.sims, icon: Activity, color: "text-slate-900" },
            { label: t("admin.totalCodes"), value: stats.codes, icon: Ticket, color: "text-amber-600" },
            { label: t("admin.activeSimulations"), value: stats.active, icon: Activity, color: "text-emerald-600" },
          ].map((s) => (
            <div key={s.label} className="bg-white border border-slate-200 rounded-lg p-5 shadow-sm animate-fade-in-up">
              <div className="flex items-center justify-between mb-3">
                <p className="text-xs font-semibold uppercase tracking-[0.15em] text-slate-400">{s.label}</p>
                <s.icon className={`w-4 h-4 ${s.color}`} />
              </div>
              <p className="text-3xl font-heading font-light text-slate-900 tracking-tighter">{s.value}</p>
            </div>
          ))}
        </div>

        <Tabs defaultValue="users">
          <TabsList className="bg-white border border-slate-200 shadow-sm">
            <TabsTrigger value="users" data-testid="tab-users">{t("admin.users")}</TabsTrigger>
            <TabsTrigger value="codes" data-testid="tab-codes">{t("admin.codes")}</TabsTrigger>
            <TabsTrigger value="simulations" data-testid="tab-simulations">{t("admin.simulations")}</TabsTrigger>
            <TabsTrigger value="trash" data-testid="tab-trash" className="flex items-center gap-1.5">
              <Archive className="w-3.5 h-3.5" />Trash{(archivedSims.length + archivedUsers.length + archivedCodes.length) > 0 && <Badge variant="destructive" className="text-[9px] px-1.5 py-0">{archivedSims.length + archivedUsers.length + archivedCodes.length}</Badge>}
            </TabsTrigger>
          </TabsList>

          {/* USERS TAB */}
          <TabsContent value="users">
            <div className="bg-white border border-slate-200 rounded-lg shadow-sm">
              <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <h2 className="text-base font-heading font-medium text-slate-900">{t("admin.users")}</h2>
                  <Badge variant="outline" className="text-xs">{users.length}</Badge>
                </div>
                {selectedUsers.size > 0 && (
                  <Button size="sm" variant="destructive" onClick={bulkDeleteUsers} data-testid="bulk-delete-users-btn">
                    <Trash2 className="w-3.5 h-3.5 mr-1.5" />{t("common.delete")} ({selectedUsers.size})
                  </Button>
                )}
              </div>
              <Table>
                <TableHeader><TableRow className="bg-slate-50/50">
                  <TableHead className="w-10"><Checkbox checked={users.length > 0 && selectedUsers.size === users.length} onCheckedChange={() => toggleAll(users, selectedUsers, setSelectedUsers)} data-testid="select-all-users" /></TableHead>
                  <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500">{t("common.name")}</TableHead>
                  <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500">{t("common.email")}</TableHead>
                  <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500">Password</TableHead>
                  <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500">{t("common.role")}</TableHead>
                  <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500">{t("common.date")}</TableHead>
                  <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500">{t("common.actions")}</TableHead>
                </TableRow></TableHeader>
                <TableBody>
                  {users.map(u => (
                    <TableRow key={u.id} className="hover:bg-slate-50/50 transition-colors">
                      <TableCell><Checkbox checked={selectedUsers.has(u.id)} onCheckedChange={() => toggle(selectedUsers, setSelectedUsers, u.id)} data-testid={`select-user-${u.id}`} /></TableCell>
                      <TableCell className="font-medium text-slate-900">{u.name}</TableCell>
                      <TableCell className="text-sm text-slate-500">{u.email}</TableCell>
                      <TableCell><code className="text-xs bg-slate-100 px-2 py-0.5 rounded font-mono text-slate-600">{u.plain_password || "—"}</code></TableCell>
                      <TableCell><Badge variant={u.role === "instructor" ? "default" : "secondary"} className="text-[10px]">{u.role}</Badge></TableCell>
                      <TableCell className="text-sm text-slate-400">{new Date(u.created_at).toLocaleDateString()}</TableCell>
                      <TableCell className="flex gap-1">
                        <Button variant="ghost" size="sm" onClick={() => resetPassword(u.id)} data-testid={`reset-pwd-${u.id}`} title={t("admin.resetPassword")}><RotateCcw className="w-3.5 h-3.5 text-slate-400" /></Button>
                        <Button variant="ghost" size="sm" onClick={() => deleteUser(u.id)} data-testid={`delete-user-${u.id}`} title={t("common.delete")}><Trash2 className="w-3.5 h-3.5 text-red-400" /></Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </TabsContent>

          {/* CODES TAB */}
          <TabsContent value="codes">
            <div className="bg-white border border-slate-200 rounded-lg shadow-sm">
              <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <h2 className="text-base font-heading font-medium text-slate-900">{t("admin.codes")}</h2>
                  <Badge variant="outline" className="text-xs">{codes.length}</Badge>
                  {selectedCodes.size > 0 && (
                    <Button size="sm" variant="destructive" onClick={bulkDeleteCodes} data-testid="bulk-delete-codes-btn" className="ml-2">
                      <Trash2 className="w-3.5 h-3.5 mr-1.5" />{t("common.delete")} ({selectedCodes.size})
                    </Button>
                  )}
                </div>
                <Button size="sm" variant="outline" onClick={() => setShowCodeDialog(true)} className="border-slate-300 text-slate-700" data-testid="create-code-btn"><Plus className="w-3.5 h-3.5 mr-1.5" />{t("admin.createCode")}</Button>
              </div>
              <Table>
                <TableHeader><TableRow className="bg-slate-50/50">
                  <TableHead className="w-10"><Checkbox checked={codes.length > 0 && selectedCodes.size === codes.length} onCheckedChange={() => toggleAll(codes, selectedCodes, setSelectedCodes)} data-testid="select-all-codes" /></TableHead>
                  <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500">{t("admin.code") || "Código"}</TableHead>
                  <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500">{t("admin.codeType")}</TableHead>
                  <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500">{t("common.role")}</TableHead>
                  <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500">{t("admin.uses")}</TableHead>
                  <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500">{t("admin.note")}</TableHead>
                  <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500">{t("common.actions")}</TableHead>
                </TableRow></TableHeader>
                <TableBody>
                  {codes.map(c => (
                    <TableRow key={c.id} className="hover:bg-slate-50/50 transition-colors">
                      <TableCell><Checkbox checked={selectedCodes.has(c.id)} onCheckedChange={() => toggle(selectedCodes, setSelectedCodes, c.id)} data-testid={`select-code-${c.id}`} /></TableCell>
                      <TableCell><code className="text-xs bg-slate-100 px-2 py-0.5 rounded font-mono text-slate-700">{c.code}</code></TableCell>
                      <TableCell><Badge variant="outline" className="text-[10px]">{c.code_type === "registration" ? t("admin.registration") : t("admin.simulationCreation")}</Badge></TableCell>
                      <TableCell className="text-sm text-slate-500">{c.role || "—"}</TableCell>
                      <TableCell className="text-sm font-mono text-slate-600">{c.uses}/{c.max_uses}</TableCell>
                      <TableCell className="text-sm text-slate-500 max-w-[200px] truncate" data-testid={`code-note-${c.id}`}>{c.note || "—"}</TableCell>
                      <TableCell><Button variant="ghost" size="sm" onClick={() => deleteCode(c.id)}><Trash2 className="w-3.5 h-3.5 text-red-400" /></Button></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </TabsContent>

          {/* SIMULATIONS TAB */}
          <TabsContent value="simulations">
            <div className="bg-white border border-slate-200 rounded-lg shadow-sm">
              <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <h2 className="text-base font-heading font-medium text-slate-900">{t("admin.simulations")}</h2>
                  <Badge variant="outline" className="text-xs">{simulations.length}</Badge>
                  {selectedSims.size > 0 && (
                    <Button size="sm" variant="destructive" onClick={bulkDeleteSims} data-testid="bulk-delete-sims-btn" className="ml-2">
                      <Trash2 className="w-3.5 h-3.5 mr-1.5" />{t("common.delete")} ({selectedSims.size})
                    </Button>
                  )}
                </div>
                <Button size="sm" variant="outline" onClick={() => setShowSimDialog(true)} className="border-slate-300 text-slate-700" data-testid="create-sim-btn"><Plus className="w-3.5 h-3.5 mr-1.5" />{t("admin.createSimulation")}</Button>
              </div>
              <Table>
                <TableHeader><TableRow className="bg-slate-50/50">
                  <TableHead className="w-10"><Checkbox checked={simulations.length > 0 && selectedSims.size === simulations.length} onCheckedChange={() => toggleAll(simulations, selectedSims, setSelectedSims)} data-testid="select-all-sims" /></TableHead>
                  <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500">{t("common.name")}</TableHead>
                  <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500">{t("admin.instructor")}</TableHead>
                  <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500">{t("common.status")}</TableHead>
                  <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500">{t("admin.rounds")}</TableHead>
                  <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500">{t("admin.hospitals")}</TableHead>
                  <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500">{t("common.actions")}</TableHead>
                </TableRow></TableHeader>
                <TableBody>
                  {simulations.map(s => (
                    <TableRow key={s.id} className="hover:bg-slate-50/50 transition-colors">
                      <TableCell><Checkbox checked={selectedSims.has(s.id)} onCheckedChange={() => toggle(selectedSims, setSelectedSims, s.id)} data-testid={`select-sim-${s.id}`} /></TableCell>
                      <TableCell className="font-medium text-slate-900">{s.name}</TableCell>
                      <TableCell className="text-sm"><span className="text-slate-700">{s.instructor_name || "—"}</span>{s.instructor_email && <span className="block text-xs text-slate-400">{s.instructor_email}</span>}</TableCell>
                      <TableCell><Badge variant={s.status === "active" ? "default" : "secondary"} className="text-[10px]">{s.status}</Badge></TableCell>
                      <TableCell className="text-sm font-mono text-slate-600">{s.current_round}/{s.rounds_count}</TableCell>
                      <TableCell className="text-sm text-slate-600">{s.hospital_count || 0}</TableCell>
                      <TableCell className="flex gap-1">
                        <Button variant="ghost" size="sm" onClick={() => navigate(`/instructor/simulation/${s.id}`)} data-testid={`view-sim-${s.id}`} title={t("common.viewHospital")}><Eye className="w-3.5 h-3.5 text-slate-400 hover:text-sky-600" /></Button>
                        <Button variant="ghost" size="sm" onClick={() => requestDeleteSim(s.id)} data-testid={`delete-sim-${s.id}`}><Trash2 className="w-3.5 h-3.5 text-red-400" /></Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </TabsContent>

          {/* TRASH TAB */}
          <TabsContent value="trash">
            <div className="space-y-4">
              {/* Archived Simulations */}
              <div className="bg-white border border-slate-200 rounded-lg shadow-sm">
                <div className="px-6 py-3 border-b border-slate-100 flex items-center gap-3">
                  <Activity className="w-4 h-4 text-slate-400" />
                  <h3 className="text-sm font-heading font-medium text-slate-700">Simulations</h3>
                  <Badge variant="outline" className="text-xs">{archivedSims.length}</Badge>
                </div>
                {archivedSims.length === 0 ? (
                  <div className="px-6 py-4 text-center text-xs text-slate-400">Empty</div>
                ) : (
                  <Table><TableBody>
                    {archivedSims.map(s => (
                      <TableRow key={s.id} className="opacity-70">
                        <TableCell className="font-medium text-slate-900">{s.name}</TableCell>
                        <TableCell className="text-sm text-slate-500">{s.instructor_name || "—"}</TableCell>
                        <TableCell className="text-sm font-mono text-slate-600">{s.current_round}/{s.rounds_count}</TableCell>
                        <TableCell className="text-xs text-slate-400">{s.archived_at ? new Date(s.archived_at).toLocaleDateString() : "—"}</TableCell>
                        <TableCell className="flex gap-1 justify-end">
                          <Button variant="outline" size="sm" onClick={() => restoreItem("simulations", s.id)} className="text-emerald-600 border-emerald-200 hover:bg-emerald-50"><ArchiveRestore className="w-3.5 h-3.5 mr-1" />Restore</Button>
                          <Button variant="ghost" size="sm" onClick={() => permanentDelete("simulations", s.id)}><Trash2 className="w-3.5 h-3.5 text-red-400" /></Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody></Table>
                )}
              </div>
              {/* Archived Users */}
              <div className="bg-white border border-slate-200 rounded-lg shadow-sm">
                <div className="px-6 py-3 border-b border-slate-100 flex items-center gap-3">
                  <Users className="w-4 h-4 text-slate-400" />
                  <h3 className="text-sm font-heading font-medium text-slate-700">Users</h3>
                  <Badge variant="outline" className="text-xs">{archivedUsers.length}</Badge>
                </div>
                {archivedUsers.length === 0 ? (
                  <div className="px-6 py-4 text-center text-xs text-slate-400">Empty</div>
                ) : (
                  <Table><TableBody>
                    {archivedUsers.map(u => (
                      <TableRow key={u.id} className="opacity-70">
                        <TableCell className="font-medium text-slate-900">{u.name}</TableCell>
                        <TableCell className="text-sm text-slate-500">{u.email}</TableCell>
                        <TableCell><Badge variant="secondary" className="text-[10px]">{u.role}</Badge></TableCell>
                        <TableCell className="text-xs text-slate-400">{u.archived_at ? new Date(u.archived_at).toLocaleDateString() : "—"}</TableCell>
                        <TableCell className="flex gap-1 justify-end">
                          <Button variant="outline" size="sm" onClick={() => restoreItem("users", u.id)} className="text-emerald-600 border-emerald-200 hover:bg-emerald-50"><ArchiveRestore className="w-3.5 h-3.5 mr-1" />Restore</Button>
                          <Button variant="ghost" size="sm" onClick={() => permanentDelete("users", u.id)}><Trash2 className="w-3.5 h-3.5 text-red-400" /></Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody></Table>
                )}
              </div>
              {/* Archived Codes */}
              <div className="bg-white border border-slate-200 rounded-lg shadow-sm">
                <div className="px-6 py-3 border-b border-slate-100 flex items-center gap-3">
                  <Ticket className="w-4 h-4 text-slate-400" />
                  <h3 className="text-sm font-heading font-medium text-slate-700">Codes</h3>
                  <Badge variant="outline" className="text-xs">{archivedCodes.length}</Badge>
                </div>
                {archivedCodes.length === 0 ? (
                  <div className="px-6 py-4 text-center text-xs text-slate-400">Empty</div>
                ) : (
                  <Table><TableBody>
                    {archivedCodes.map(c => (
                      <TableRow key={c.id} className="opacity-70">
                        <TableCell><code className="text-xs bg-slate-100 px-2 py-0.5 rounded font-mono">{c.code}</code></TableCell>
                        <TableCell className="text-sm text-slate-500">{c.role || "—"}</TableCell>
                        <TableCell className="text-xs text-slate-400">{c.archived_at ? new Date(c.archived_at).toLocaleDateString() : "—"}</TableCell>
                        <TableCell className="flex gap-1 justify-end">
                          <Button variant="outline" size="sm" onClick={() => restoreItem("codes", c.id)} className="text-emerald-600 border-emerald-200 hover:bg-emerald-50"><ArchiveRestore className="w-3.5 h-3.5 mr-1" />Restore</Button>
                          <Button variant="ghost" size="sm" onClick={() => permanentDelete("codes", c.id)}><Trash2 className="w-3.5 h-3.5 text-red-400" /></Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody></Table>
                )}
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </div>

      {/* CONFIRM DELETE DIALOG */}
      <Dialog open={!!confirmDelete} onOpenChange={() => setConfirmDelete(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-500" />
              Confirm Archive / Confirmar Archivo
            </DialogTitle>
          </DialogHeader>
          {confirmDelete && (
            <div className="space-y-3 py-2">
              {confirmDelete.type === "simulation" && (
                <>
                  <p className="text-sm text-slate-700">You are about to archive simulation <strong>{confirmDelete.name}</strong>:</p>
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 space-y-1">
                    <p className="text-sm font-medium text-amber-800">{confirmDelete.hospitals} hospitals</p>
                    <p className="text-sm font-medium text-amber-800">{confirmDelete.decisions} decisions</p>
                    <p className="text-sm font-medium text-amber-800">{confirmDelete.round_results} round results</p>
                  </div>
                </>
              )}
              {confirmDelete.type === "user" && (
                <>
                  <p className="text-sm text-slate-700">You are about to archive user <strong>{confirmDelete.name}</strong> ({confirmDelete.email}):</p>
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 space-y-1">
                    <p className="text-sm font-medium text-amber-800">Role: {confirmDelete.role}</p>
                    <p className="text-sm font-medium text-amber-800">{confirmDelete.hospitals} hospital memberships</p>
                  </div>
                </>
              )}
              {confirmDelete.type === "code" && (
                <p className="text-sm text-slate-700">You are about to archive this invite code. It will be moved to trash.</p>
              )}
              <p className="text-xs text-slate-500">
                It will be moved to Trash. You can restore it later. /
                Se moverá a la Papelera. Puedes restaurarlo después.
              </p>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmDelete(null)}>{t("common.cancel")}</Button>
            <Button variant="destructive" onClick={confirmDeleteAction} data-testid="confirm-archive-btn">
              <Archive className="w-4 h-4 mr-1.5" />Archive / Archivar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* CREATE CODE DIALOG */}
      <Dialog open={showCodeDialog} onOpenChange={setShowCodeDialog}>
        <DialogContent><DialogHeader><DialogTitle className="font-heading">{t("admin.createCode")}</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div><Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">{t("admin.codeType")}</Label>
              <Select value={codeForm.code_type} onValueChange={v => setCodeForm({...codeForm, code_type: v})}><SelectTrigger className="mt-1.5"><SelectValue /></SelectTrigger><SelectContent>
                <SelectItem value="registration">{t("admin.registration")}</SelectItem>
                <SelectItem value="simulation_creation">{t("admin.simulationCreation")}</SelectItem>
              </SelectContent></Select></div>
            {codeForm.code_type === "registration" && (
              <div><Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">{t("common.role")}</Label>
                <Select value={codeForm.role} onValueChange={v => setCodeForm({...codeForm, role: v})}><SelectTrigger className="mt-1.5"><SelectValue /></SelectTrigger><SelectContent>
                  <SelectItem value="student">{t("auth.student")}</SelectItem>
                  <SelectItem value="instructor">{t("auth.instructor")}</SelectItem>
                </SelectContent></Select></div>
            )}
            <div><Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">{t("admin.maxUses")}</Label>
              <Input type="number" min="1" value={codeForm.max_uses} onChange={e => setCodeForm({...codeForm, max_uses: parseInt(e.target.value) || 1})} className="mt-1.5" /></div>
            <div><Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">{t("admin.note")}</Label>
              <Input value={codeForm.note} onChange={e => setCodeForm({...codeForm, note: e.target.value})} className="mt-1.5" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCodeDialog(false)}>{t("common.cancel")}</Button>
            <Button onClick={createCode} className="bg-slate-900 hover:bg-slate-800" data-testid="confirm-create-code">{t("common.create")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* CREATE SIMULATION DIALOG */}
      <Dialog open={showSimDialog} onOpenChange={setShowSimDialog}>
        <DialogContent><DialogHeader><DialogTitle className="font-heading">{t("admin.createSimulation")}</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div><Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">{t("common.name")}</Label>
              <Input value={simForm.name} onChange={e => setSimForm({...simForm, name: e.target.value})} placeholder="Hospital Sim 2026" className="mt-1.5" data-testid="sim-name-input" /></div>
            <div><Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">{t("admin.assignTo")}</Label>
              <Select value={simForm.instructor_id} onValueChange={v => setSimForm({...simForm, instructor_id: v})}><SelectTrigger className="mt-1.5"><SelectValue placeholder={t("admin.assignTo")} /></SelectTrigger><SelectContent>
                {instructors.map(i => <SelectItem key={i.id} value={i.id}>{i.name} ({i.email})</SelectItem>)}
              </SelectContent></Select></div>
            <div className="grid grid-cols-2 gap-4">
              <div><Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">{t("admin.rounds")}</Label>
                <Input type="number" min="4" max="20" value={simForm.rounds_count} onChange={e => setSimForm({...simForm, rounds_count: parseInt(e.target.value) || 8})} className="mt-1.5" /></div>
              <div><Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">{t("admin.maxTeams")}</Label>
                <Input type="number" min="2" max="16" value={simForm.max_teams} onChange={e => setSimForm({...simForm, max_teams: parseInt(e.target.value) || 8})} className="mt-1.5" /></div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSimDialog(false)}>{t("common.cancel")}</Button>
            <Button onClick={createSimulation} className="bg-slate-900 hover:bg-slate-800" data-testid="confirm-create-sim">{t("common.create")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
